"use client";

import { GoogleIcon, Logo } from "@/app/_components/icons";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/app/_components/ui/form";
import { LoginFormValues, loginSchema } from "@/app/_lib/schemas/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn, getSession } from "next-auth/react";
import { getPostLoginRedirect } from "@/app/_lib/auth/post-login-redirect";
import Link from "next/link";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useRouter } from "next/navigation";
import { toast } from "@/app/_hooks/use-toast";

export default function LoginForm() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
    mode: "onChange",
  });

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setLoading(true);
      const res = await signIn("credentials", {
        email: data.email,
        password: data.password,
        redirect: false,
      });
      if (res?.ok) {
        // Keep the loader spinning until we're actually on the next page —
        // the session (and its role) isn't available from `res` itself, so
        // fetch it before deciding where staff vs. customers land.
        const session = await getSession();
        router.push(getPostLoginRedirect(session?.user?.role));
      } else {
        setLoading(false);
        toast({
          variant: "destructive",
          title: "Login failed",
          description: "Invalid email or password.",
        });
      }
    } catch {
      toast({
        variant: "destructive",
        title: "Login failed",
        description: "Something went wrong. Try again.",
      });
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      await signIn("google");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center flex-1 px-6 py-16 bg-background min-h-screen">
      <div className="w-full max-w-[380px] flex flex-col gap-7">
        {/* Mobile logo */}
        <Link href="/" className="lg:hidden text-[28px] hover:opacity-80 transition-opacity">
          <Logo />
        </Link>

          <div>
            <h1 className="text-[28px] font-semibold tracking-tight leading-tight">
              Welcome back
            </h1>
            <p className="text-muted-foreground mt-1.5 text-[15px]">
              Sign in to your Busyexpand account
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-3.5"
            >
              <FormField
                name="email"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <input
                        type="email"
                        placeholder="Email address"
                        {...field}
                        className={`w-full px-4 py-3 text-sm border rounded-xl bg-background outline-none transition-colors ${
                          form.formState.errors.email
                            ? "border-red-400"
                            : "border-border focus:border-primary"
                        }`}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="password"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          {...field}
                          className={`w-full px-4 py-3 text-sm border rounded-xl bg-background outline-none transition-colors pr-10 ${
                            form.formState.errors.password
                              ? "border-red-400"
                              : "border-border focus:border-primary"
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end -mt-1">
                <Link href="/forgot-password" className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          </Form>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            type="button"
            disabled={googleLoading}
            onClick={handleGoogleSignIn}
            className="w-full py-3 flex items-center justify-center gap-3 text-sm border border-border rounded-xl hover:bg-accent transition-colors disabled:opacity-60"
          >
            {googleLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Connecting…
              </>
            ) : (
              <>
                <GoogleIcon />
                Continue with Google
              </>
            )}
          </button>

          <p className="text-center text-sm text-muted-foreground">
            Don&apos;t have an account?{" "}
            <Link
              href="/signup"
              className="text-primary font-medium hover:underline"
            >
              Create account
            </Link>
          </p>
      </div>
    </div>
  );
}
